import React from 'react'
import Decimal from 'decimal.js'
import lang from '@/lang'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import Info from '@/modules/base/components/info/info'
import getStateInfo from '@/modules/invoice/helpers/getStateInfo'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import Badge from '@/modules/base/components/badge/badge'
import { invoiceStates, transactionTypes } from '@/modules/base/util/constants'
import InfoItem from '@/modules/base/components/info/info-item'
import { useNavigate } from 'react-router'

const { TRANSACTION_TYPE_CREDIT_NOTE } = transactionTypes

const EMPTY_PROP = '–'

const rowCount = {
  single: 1,
  multi: 3,
}

const { PARTIALLYPAID } = invoiceStates

const InvoiceInfo = (props) => {
  const navigate = useNavigate()
  const { transaction, isOverDue, variant, type, absolute } = props
  const classes = useStyles({ variant, totalGross: transaction?.totalGross })
  const totalGrossDisplay = absolute ? new Decimal(transaction?.totalGross).abs().toNumber() : transaction?.totalGross
  const outstandingAmountDisplay = absolute ? new Decimal(transaction?.outstandingAmount).abs().toNumber() : transaction?.outstandingAmount

  const stateInfo = getStateInfo(transaction.state, isOverDue, transaction.outstandingAmount, transaction.totalGross)
  let stateInfoPartiallyPaid
  if (transaction.state === PARTIALLYPAID && isOverDue) {
    stateInfoPartiallyPaid = getStateInfo(transaction.state, false, transaction.outstandingAmount, transaction.totalGross)
  }

  const infoItems = [
    { label: type === TRANSACTION_TYPE_CREDIT_NOTE ? lang.payments.paymentDrawer.creditNote.number : lang.payments.paymentDrawer.invoice.number, value: transaction?.number || EMPTY_PROP },
    { label: type === TRANSACTION_TYPE_CREDIT_NOTE ? lang.payments.paymentDrawer.creditNote.date : lang.payments.paymentDrawer.invoice.date, value: transaction?.date || EMPTY_PROP },
  ]

  if (type !== TRANSACTION_TYPE_CREDIT_NOTE) {
    infoItems.push({ label: lang.payments.paymentDrawer.invoice.dueTo, value: transaction?.dueToDate || EMPTY_PROP })
  }

  const onCustomerClick = (id) => {
    navigate(`/customer/${id}`)
  }

  return (
    <>
      <div>
        <div className={classes.badgeContainer}>
          <Badge label={stateInfo.label} color={stateInfo.color} customClass={classes.badge} customClassLabel={classes.badgeLabel} />
          {stateInfoPartiallyPaid && <Badge label={stateInfoPartiallyPaid.label} color={stateInfoPartiallyPaid.color} customClass={classes.badge} customClassLabel={classes.badgeLabel} />}
        </div>
        <div className={classes.priceRow}>
          <InfoItem infoItem={{ label: lang.payments.paymentDrawer.total, value: formatCurrency(totalGrossDisplay) }} classNameValue={classes.price} />
          {transaction?.totalGross !== transaction?.outstandingAmount
            && <InfoItem infoItem={{ label: lang.payments.paymentDrawer.openAmount, value: formatCurrency(outstandingAmountDisplay) }} classNameValue={classes.price} />
          }
        </div>
        <div className={classes.customerName} onClick={() => onCustomerClick(transaction.customerId)}>{transaction?.customerData?.name || ''}</div>
      </div>
      <div className={classes.line} />
      <Info infoItems={infoItems} className={classes.infoWrapper} classNameContainer={classes.infoContainer} rowCount={rowCount[variant]} />
      <div className={classes.line} />
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  priceRow: ({ variant }) => ({
    display: 'flex',
    flexDirection: variant === 'single' ? 'column' : 'row',
    paddingTop: 20,
    justifyContent: 'space-between',
    ...(variant === 'single' && {
      width: 'fit-content',
      gap: '20px',
    }),
  }),
  price: {
    color: theme.black,
    fontSize: '28px',
    letterSpacing: 0.56,
  },
  customerName: {
    color: theme.primary,
    fontSize: theme.fontSizes.smedium,
    paddingTop: 20,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  line: {
    width: '100%',
    height: '1px',
    margin: '20px 0px',
    backgroundColor: theme.borderColor,
  },
  infoWrapper: {
    padding: '0 !important',
  },
  infoContainer: {
    gap: 20,
  },
  badge: {
    width: 'fit-content',
  },
  badgeLabel: {
    padding: '0px 10px',
  },
  badgeContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
}))

InvoiceInfo.propTypes = {
  absolute: PropTypes.bool,
  variant: PropTypes.oneOf(['single', 'multi']),
  isOverDue: PropTypes.bool,
  transaction: PropTypes.object,
}

InvoiceInfo.defaultProps = {
  variant: 'multi',
}

export default InvoiceInfo
